import { Constants } from '../../internal/Constants';
import { FormatPhoneE164 } from '../../internal/Helpers';
import { Localized } from '../../internal/Localized';
import { useLobbySettings } from '../../providers/LobbySettingsProvider';
import { useLocale } from '../../providers/LocaleProvider';
import Styles from './Landing.module.css';
import { LandingConstants } from './LandingConstants';
import { Image, Label } from '@app/engine';
import { Settings, Telemetry } from '@shared/frontend';
import { useMemo } from 'react';
import type { INicheContentDTO } from '@shared/common';

/** File-local constants for the demo card (decorative glyphs, the versioned favicon path and telemetry key). */
const localConstants = {
  faviconPath: 'sprites/favicon.png',
  telScheme: 'tel:',
  smsScheme: 'sms:',
  callTelemetryKey: 'landing_demo_call',
  textTelemetryKey: 'landing_demo_text',
  checkGlyph: '✓'
} as const;

/** Lobby-served favicon URL (versioned) reused as the demo agent avatar — mirrors the portal LoginForm favicon pattern. */
const faviconUrl = `${import.meta.env.BASE_URL}${localConstants.faviconPath}?v=${Settings.VERSION}`;

/** Props for the demo card. */
interface IDemoCardProps {
  niches: INicheContentDTO[];
}

/**
 * Card 2 — live demo. Invites the visitor to call the public demo line and hear Echo answer. The status, call CTA and tags react to the configured demo number and niche catalog: with a number
 * set the line reads live and the CTA dials it; without one the line reads "not live yet" and the CTA is a subtly-disabled "Try Later".
 *
 * @param props - Component props (the live niche catalog supplying the demo tags).
 * @returns The demo card element.
 */
export function DemoCard(props: IDemoCardProps) {
  const { demoE164 } = useLobbySettings();
  const { langVersion } = useLocale();
  const live = demoE164.trim().length > 0;
  // Tags mirror the hero ticker selection but carry the human-readable signup label (per product: "Show in ticker & demo tags").
  const tags = useMemo(() => props.niches.filter((entry) => entry.showInTicker).map((entry) => entry.signupLabel), [props.niches]);
  const waveBars = useMemo(() => Array.from({ length: LandingConstants.demoWaveformBars }, (_unused, index) => index), []);
  // Re-read the checklist copy on every language change so the demo panel reflows with the rest of the page (previously captured once at module load, so it stayed English).
  const demoChecklist = useMemo(() => {
    void langVersion;
    return [Localized.LandingDemoCheck1, Localized.LandingDemoCheck2, Localized.LandingDemoCheck3, Localized.LandingDemoCheck4];
  }, [langVersion]);
  const callLabel = `${Localized.LandingDemoCallPrefix}${FormatPhoneE164(demoE164)}`;
  const textLabel = `${Localized.LandingDemoTextPrefix}${FormatPhoneE164(demoE164)}`;

  return (
    <section className={`${Styles.cardInner} ${Styles.cardDemo}`}>
      <div className={Styles.cardTag}>
        <Label text={Localized.LandingDemoLabel} classNameContainer={Styles.sectionLabel} />
        {/* Structural wrapper: a single Label cannot render the two-line styled headline, so a div is used and each line carries the tooltip. */}
        <div className={Styles.sectionTitle}>
          <Label text={Localized.LandingDemoTitle1} title={`${Localized.LandingDemoTitle1} ${Localized.LandingDemoTitle2}`} />
          <br />
          <Label text={Localized.LandingDemoTitle2} title={`${Localized.LandingDemoTitle1} ${Localized.LandingDemoTitle2}`} classNameContainer={Styles.accent2} />
        </div>
        <Label text={Localized.LandingDemoSub} classNameContainer={Styles.demoSub} />
      </div>

      <div className={Styles.cardBody}>
        <div className={Styles.demoPanel}>
          <div className={Styles.demoHead}>
            <Image title={Constants.Name} spriteUrl={faviconUrl} classNameContainer={Styles.demoAvatar} classNameImageEnabled={Styles.demoAvatarImg} />
            <div className={Styles.demoIdentity}>
              <Label text={Constants.Name} classNameContainer={Styles.demoName} />
              <Label text={Localized.LandingDemoAgentRole} classNameContainer={Styles.demoRole} />
            </div>
            <div className={`${Styles.demoStatus} ${live ? Styles.demoStatusLive : Styles.demoStatusOffline}`}>
              {/* Raw <span>: empty decorative status dot flagged aria-hidden; engine Label requires text and cannot set aria-hidden. */}
              <span className={Styles.demoStatusDot} aria-hidden="true" />
              <Label text={live ? Localized.LandingDemoStatusLive : Localized.LandingDemoStatusOffline} classNameContainer={Styles.demoStatusText} />
            </div>
          </div>

          <div className={Styles.demoWave} aria-hidden="true">
            {waveBars.map((index) => (
              // Raw <span>: waveform bar needs a per-index inline animation-delay style, which engine Label does not support.
              <span key={index} className={Styles.demoWaveBar} style={{ animationDelay: `${index * LandingConstants.demoWaveformDelayStepMs}ms` }} />
            ))}
          </div>

          <ul className={Styles.demoChecklist}>
            {demoChecklist.map((item) => (
              <li key={item} className={Styles.demoCheckItem}>
                {/* Raw <span>: decorative check glyph flagged aria-hidden; engine Label cannot set aria-hidden and would add a glyph title tooltip. */}
                <span className={Styles.demoCheckMark} aria-hidden="true">
                  {localConstants.checkGlyph}
                </span>
                <Label text={item} classNameContainer={Styles.demoCheckText} />
              </li>
            ))}
          </ul>

          {tags.length > 0 ? (
            <div className={Styles.demoTagsWrap}>
              <Label text={Localized.LandingDemoTagsLabel} classNameContainer={Styles.demoTagsLabel} />
              <div className={Styles.demoTags}>
                {tags.map((tag, index) => (
                  // Raw <span>: demo pill needs per-index inline animation styles and wraps a child Label; no engine control supports both.
                  <span
                    key={tag}
                    className={Styles.demoPill}
                    style={{ animationDuration: `${tags.length * LandingConstants.demoTagDelayStepMs}ms`, animationDelay: `${index * LandingConstants.demoTagDelayStepMs}ms` }}
                  >
                    <Label text={tag} classNameContainer={Styles.demoPillText} />
                  </span>
                ))}
              </div>
            </div>
          ) : null}

          {live ? (
            // Native anchors: the engine Button cannot emit `tel:`/`sms:` hrefs, so real <a> elements are the correct choice for the click-to-call / click-to-text CTA pair.
            <div className={Styles.demoCtaRow}>
              <a className={Styles.demoCall} href={`${localConstants.telScheme}${demoE164}`} onClick={() => Telemetry.FeatureUsed(localConstants.callTelemetryKey)}>
                <Label text={callLabel} classNameContainer={Styles.demoCallText} />
              </a>
              <a className={`${Styles.demoCall} ${Styles.demoText}`} href={`${localConstants.smsScheme}${demoE164}`} onClick={() => Telemetry.FeatureUsed(localConstants.textTelemetryKey)}>
                <Label text={textLabel} classNameContainer={`${Styles.demoCallText} ${Styles.demoTextText}`} />
              </a>
            </div>
          ) : (
            // Raw <span>: static disabled CTA look-alike with aria-disabled wrapping a child Label; engine Button would render an interactive button with text only.
            <span className={`${Styles.demoCall} ${Styles.demoCallDisabled}`} aria-disabled="true">
              <Label text={Localized.LandingDemoCallDisabled} classNameContainer={Styles.demoCallText} />
            </span>
          )}

          <Label text={Localized.LandingDemoCaption} classNameContainer={Styles.demoCaption} />
        </div>
      </div>
    </section>
  );
}
